import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportApi, mailApi, authApi, myCustomerApi } from '../api/client';
import useAuthStore from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';

const RECENT_MAILS_LIMIT = 10;
const STORAGE_WARN_PCT = 0.9; // ab 90% Warnung (amber), ab 100% kritisch (rot)

export default function PortalDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [reportStats, setReportStats] = useState(null);
  const [recentMails, setRecentMails] = useState([]);
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    // Falls /me/ beim Login fehlschlug und customer-Objekt fehlt, Profil nachladen
    if (!user.customer) {
      authApi.me()
        .then(({ data }) => useAuthStore.setState({ user: data }))
        .catch(() => setLoading(false));
      return;
    }
    // Separate Calls: ein Fehler blockiert nicht den anderen
    Promise.allSettled([
      reportApi.stats(),
      mailApi.list({ page_size: RECENT_MAILS_LIMIT }),
      myCustomerApi.get(),
    ]).then(([statsResult, mailsResult, customerResult]) => {
      if (statsResult.status === 'fulfilled') setReportStats(statsResult.value.data);
      if (mailsResult.status === 'fulfilled') {
        // Backend-Pagination ignoriert page_size aktuell (fixe PAGE_SIZE=50) —
        // hier zusätzlich hart auf RECENT_MAILS_LIMIT begrenzen.
        const list = mailsResult.value.data.results || mailsResult.value.data;
        setRecentMails(list.slice(0, RECENT_MAILS_LIMIT));
      }
      if (customerResult.status === 'fulfilled') {
        const { storage_used_mb, storage_limit_mb } = customerResult.value.data;
        if (storage_limit_mb > 0) setStorageInfo({ used: storage_used_mb, limit: storage_limit_mb });
      }
    }).finally(() => setLoading(false));
  }, [user, refreshTick]);

  // Bei eingehendem Push bzw. Klick auf eine Benachrichtigung (siehe
  // PortalLayout.jsx / sw.js) das Dashboard automatisch neu laden, statt
  // dass der Nutzer manuell neu laden muss.
  useEffect(() => {
    const handleRefresh = () => setRefreshTick(t => t + 1);
    window.addEventListener('incomail:refresh-mails', handleRefresh);
    return () => window.removeEventListener('incomail:refresh-mails', handleRefresh);
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;

  const totalLast30 = reportStats ? reportStats.volume.reduce((s, v) => s + v.count, 0) : null;
  const totalAllTime = reportStats ? reportStats.categories.reduce((s, c) => s + c.count, 0) : null;
  const categoryData = reportStats?.categories || [];
  const chartData = {
    labels: Object.keys(categoryData),
    datasets: [{
      label: 'E-Mails',
      data: Object.values(categoryData),
      backgroundColor: '#3b82f6',
      borderRadius: 6,
    }],
  };

  const storagePct = storageInfo && storageInfo.limit > 0 ? storageInfo.used / storageInfo.limit : 0;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Speicherlimit-Warnung */}
      {storageInfo && storagePct >= STORAGE_WARN_PCT && (
        <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
          storagePct >= 1
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          <div>
            <strong>{storagePct >= 1 ? 'Speicherlimit überschritten:' : 'Speicherlimit fast erreicht:'}</strong>{' '}
            {storageInfo.used.toLocaleString('de-DE')} MB von {storageInfo.limit.toLocaleString('de-DE')} MB belegt
            ({Math.round(storagePct * 100)}%).
          </div>
          <div>
            Bitte nicht mehr benötigte Anhänge löschen oder die Aufbewahrungsfrist verkürzen; wende
            dich bei Bedarf an deinen Administrator, um das Limit anzupassen.
          </div>
        </div>
      )}

      {/* Kennzahlen */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { label: 'E-Mails (letzte 30 Tage)', value: totalLast30?.toLocaleString('de-DE') ?? '–', color: 'blue' },
          { label: 'E-Mails gesamt', value: totalAllTime?.toLocaleString('de-DE') ?? '–', color: 'indigo' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="text-sm text-gray-500">{label}</div>
            <div className={`text-2xl font-bold mt-1 text-${color}-600`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Kategorienverteilung */}
      {categoryData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <h2 className="font-semibold mb-4 text-gray-900">Kategorienverteilung</h2>
          <div className="space-y-2">
            {categoryData.map(({ name, count }) => {
              const total = categoryData.reduce((s, c) => s + c.count, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={name} className="flex items-center gap-3 text-sm">
                  <div className="w-32 text-gray-700 truncate">{name}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-8 text-right text-gray-500 text-xs">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Letzte E-Mails */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Zuletzt verarbeitete E-Mails</h2>
        </div>
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[34%]" />
            <col className="w-[44%]" />
            <col className="w-[22%]" />
          </colgroup>
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              {['Absender', 'Betreff', 'Empfangen'].map(h => (
                <th key={h} className="px-2 sm:px-4 py-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {recentMails.map(m => (
              <tr
                key={m.id}
                onClick={() => navigate(`/portal/mails?open=${m.id}`)}
                className={`cursor-pointer hover:bg-blue-50 ${!m.is_read ? 'font-medium' : ''}`}
              >
                <td className="px-2 sm:px-4 py-3 text-xs truncate">{m.sender}</td>
                <td className="px-2 sm:px-4 py-3 truncate">{m.subject || '(kein Betreff)'}</td>
                <td className="px-2 sm:px-4 py-3 text-xs text-gray-500 truncate">
                  {m.received_at ? new Date(m.received_at).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '–'}
                </td>
              </tr>
            ))}
            {recentMails.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Noch keine E-Mails verarbeitet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
